import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { Reveal } from "@/components/reveal";
import { Section, SectionTitle } from "@/components/section";
import { CtaBand } from "@/components/cta-band";
import { ResponsiveImg } from "@/components/responsive-img";
import { SERVICES, PROCESS } from "@/lib/site-data";
import { lcpPreload, imgSizes } from "@/lib/images";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Framing Services | Magnolia House, Fuquay-Varina NC" },
      {
        name: "description",
        content:
          "Custom framing, shadow boxes, archival and museum-quality framing, canvas stretching, photo restoration, needlepoint lacing, mat design and hanging service in Fuquay-Varina.",
      },
      {
        property: "og:title",
        content: "Framing Services | Magnolia House Custom Picture Framing",
      },
      {
        property: "og:description",
        content:
          "Ten framing services designed around your piece — from diplomas to jerseys, flags to heirloom photographs.",
      },
    ],
    links: [lcpPreload("mat-designs")],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Services"
        title={
          <>
            Everything a piece <span className="amber-text italic">needs</span>, under one roof.
          </>
        }
        lead="Design, materials, construction and hanging — handled in the shop by the same certified framer from first conversation to finished wall."
        image="mat-designs"
        alt="Custom mat designs at Magnolia House"
      />

      <Section>
        <div className="grid gap-y-20">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title}>
              <article
                className={`grid items-center gap-10 md:grid-cols-2 ${
                  i % 2 ? "md:[direction:rtl]" : ""
                }`}
              >
                <div className="plate [direction:ltr]">
                  <ResponsiveImg
                    name={s.image}
                    sizes={imgSizes.half}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
                <div className="min-w-0 [direction:ltr]">
                  <p className="eyebrow">
                    {String(i + 1).padStart(2, "0")} / {SERVICES.length}
                  </p>
                  <h2 className="display mt-4 text-4xl sm:text-5xl">{s.title}</h2>
                  <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="mist">
        <SectionTitle eyebrow="The process" title="What working together looks like." />
        <ol className="mt-14 grid gap-8 md:grid-cols-3 lg:grid-cols-5">
          {PROCESS.map((p, i) => (
            <Reveal as="li" key={p.step} delay={i * 70}>
              <p className="display text-5xl text-[var(--terracotta)]">{p.step}</p>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.16em]">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </Reveal>
          ))}
        </ol>
      </Section>

      <CtaBand />
    </>
  );
}
